import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { ConciergeRequestBar } from './ConciergeRequestBar';
import { heroScenes, heroVideo } from '../data/content';
import { useLanguage } from '../contexts/LanguageContext';
const SCENE_MS = 6000;
const FADE_S = 0.9;
const BLEND_EASE = [0.65, 0, 0.35, 1] as const;
type Move = {
  scale: number;
  x: string;
  y: string;
};

// One distinct camera move per shot — dolly-in, tracking, crane, pull-back
const cameraMoves: {
  from: Move;
  to: Move;
}[] = [{
  from: {
    scale: 1.18,
    x: '-3%',
    y: '0%'
  },
  to: {
    scale: 1.05,
    x: '2.5%',
    y: '-0.5%'
  }
}, {
  from: {
    scale: 1.05,
    x: '0%',
    y: '2%'
  },
  to: {
    scale: 1.2,
    x: '-1%',
    y: '-1.5%'
  }
}, {
  from: {
    scale: 1.14,
    x: '3.5%',
    y: '0%'
  },
  to: {
    scale: 1.14,
    x: '-3.5%',
    y: '0.5%'
  }
}, {
  from: {
    scale: 1.08,
    x: '1%',
    y: '0%'
  },
  to: {
    scale: 1.26,
    x: '-1.5%',
    y: '1%'
  }
}, {
  from: {
    scale: 1.18,
    x: '0%',
    y: '-2.5%'
  },
  to: {
    scale: 1.06,
    x: '0%',
    y: '2%'
  }
}];
const still: Move = {
  scale: 1.06,
  x: '0%',
  y: '0%'
};
export function Hero() {
  const reduceMotion = useReducedMotion();
  const {
    t
  } = useLanguage();
  const reveal = (delay: number) => reduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 14
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 1.3,
      ease: [0.22, 1, 0.36, 1],
      delay
    }
  };
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const singleVideoRef = useRef<HTMLVideoElement>(null);
  const clipRefs = useRef<(HTMLVideoElement | null)[]>([]);
  // The film is only requested once the page itself has finished loading,
  // and only revealed once it can actually play — the stills hold the frame until then.
  const [videoMounted, setVideoMounted] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  useEffect(() => {
    if (reduceMotion) setPlaying(false);
  }, [reduceMotion]);
  useEffect(() => {
    if (!heroVideo) return;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const mount = () => {
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: {
          timeout: number;
        }) => number;
      };
      if (w.requestIdleCallback) idleId = w.requestIdleCallback(() => setVideoMounted(true), {
        timeout: 1500
      });else timeoutId = window.setTimeout(() => setVideoMounted(true), 300);
    };
    // Safety net: never wait more than 2.5s for the page's load event before starting the film
    const safety = window.setTimeout(() => setVideoMounted(true), 2500);
    if (document.readyState === 'complete') mount();else window.addEventListener('load', mount, {
      once: true
    });
    return () => {
      window.removeEventListener('load', mount);
      const w = window as Window & {
        cancelIdleCallback?: (id: number) => void;
      };
      if (idleId !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      window.clearTimeout(safety);
    };
  }, []);

  // Advance scenes (until the film takes over)
  useEffect(() => {
    if (videoReady || !playing) return;
    const id = window.setTimeout(() => setIndex(i => (i + 1) % heroScenes.length), SCENE_MS);
    return () => window.clearTimeout(id);
  }, [index, playing]);

  // Play the active clip from its start, pause the rest
  useEffect(() => {
    clipRefs.current.forEach((clip, i) => {
      if (!clip) return;
      if (i === index && playing) {
        clip.currentTime = 0;
        void clip.play().catch(() => undefined);
      } else if (i !== index) {
        window.setTimeout(() => clip.pause(), FADE_S * 1000);
      } else {
        clip.pause();
      }
    });
  }, [index, playing]);
  useEffect(() => {
    const video = singleVideoRef.current;
    if (!video) return;
    if (playing) void video.play().catch(() => undefined);else video.pause();
  }, [playing, videoMounted]);

  // Mobile: slightly shorter than the screen (minus the black bar above) so the request bar peeks in below
  return <section id="top" aria-label={t('hero.label')} className="relative -mt-[72px] w-full overflow-hidden bg-ink pb-20 text-paper md:-mt-[92px] md:pb-24 lg:z-10 lg:overflow-visible lg:pb-32" data-id="element-547" data-mp="3vhfew8ckw3jr">
      {/* Anchor for all "contact / request" links — lands on the request bar */}
      <span id="consultation" className="absolute top-0" aria-hidden="true" data-id="element-548" data-mp="3hqk1cir4wy9u" />
      {/* Film is clipped here so the request panel's dropdowns can extend past the hero on desktop */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-id="element-549" data-mp="3rgvfsz8g2puz">
      <motion.div className="absolute inset-0" initial={reduceMotion ? false : {
        opacity: 0,
        scale: 1.04
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 2.4,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-550" data-mp="3mp3nok9n4c2q">
      {heroVideo && videoMounted && <motion.video ref={singleVideoRef} src={heroVideo} muted loop playsInline autoPlay={playing} preload="auto" aria-hidden="true" onLoadedData={() => setVideoReady(true)} onCanPlay={e => {
          setVideoReady(true);
          if (playing) void e.currentTarget.play().catch(() => undefined);
        }} className="absolute inset-0 z-[1] h-full w-full object-cover" initial={{
          opacity: 0
        }} animate={{
          opacity: videoReady ? 1 : 0
        }} transition={{
          duration: 1.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-551" data-mp="19ixbbwxsbc90" />}
      {<div className="absolute inset-0 z-0" aria-hidden="true" data-id="element-552" data-mp="1jajhvfz6gn5f">
          {heroScenes.map((s, i) => {
            const active = i === index;
            const move = cameraMoves[i % cameraMoves.length];
            return <motion.div key={s.image} className="absolute inset-0" style={{
              zIndex: active ? 1 : 0
            }} initial={false} animate={active ? {
              opacity: 1,
              filter: 'blur(0px) brightness(1)'
            } : {
              opacity: 0,
              filter: 'blur(6px) brightness(0.5)'
            }} transition={{
              duration: FADE_S,
              ease: BLEND_EASE
            }} data-id="element-553" data-mp="bun4n4n200bk" data-mpi={i}>
                {s.video ? <video ref={el => {
                clipRefs.current[i] = el;
              }} src={s.video} poster={s.image} muted loop playsInline preload="auto" className="h-full w-full object-cover" data-id="element-554" data-mp="1vrcgt59u0l1q" /> :
              // Handheld sway layered over the camera move so the still breathes like footage
              <motion.div className="h-full w-full" animate={reduceMotion || !playing ? {
                x: 0,
                y: 0,
                rotate: 0
              } : {
                x: [0, 6, -4, 3, 0],
                y: [0, -4, 3, -2, 0],
                rotate: [0, 0.15, -0.1, 0.08, 0]
              }} transition={{
                duration: 7,
                ease: 'easeInOut',
                repeat: Infinity
              }} data-id="element-555" data-mp="3b94h6qtbeyqg">
                    <motion.img src={s.image} alt="" className="h-full w-full object-cover will-change-transform" initial={reduceMotion ? still : move.from} animate={reduceMotion ? still : active ? move.to : move.from} transition={active ? {
                  duration: SCENE_MS / 1000 + FADE_S,
                  ease: [0.33, 0, 0.67, 1]
                } : {
                  duration: 0,
                  delay: FADE_S
                }} data-id="element-556" data-mp="1coulo6wj4sm" />
                  </motion.div>}
              </motion.div>;
          })}
        </div>}
      </motion.div>

      {/* Film texture: animated grain + gentle exposure flicker */}
      <div className="film-grain pointer-events-none absolute inset-[-50%] z-[2] opacity-[0.09] motion-reduce:hidden" aria-hidden="true" data-id="element-557" data-mp="1b05qyd2ftr7l" />
      <div className="film-flicker pointer-events-none absolute inset-0 z-[2] bg-ink motion-reduce:hidden" aria-hidden="true" data-id="element-558" data-mp="1k2f0p0pvtgjx" />
      <div className="absolute inset-0 z-[2] bg-ink/45" aria-hidden="true" data-id="element-559" data-mp="1ypzzwjjmyvmk" />
      </div>

      {/* Request panel — directly beneath the navigation (desktop) */}
      <div className="absolute inset-x-0 top-[124px] z-[5] hidden px-6 lg:block" data-id="element-560" data-mp="27shnb1b11ub5">
        {/* 1264px content width, as on Lufthansa — the panel's own padding sits outside it */}
        <div className="mx-auto max-w-[1344px]" data-id="element-561" data-mp="ddykexmkposl">
          <ConciergeRequestBar idPrefix="req-top" className="px-10 py-8" data-id="element-562" data-mp="deqg08g67miv" />
        </div>
      </div>

      {/* Copy set straight on the film — top-left on mobile/tablet, beneath the request panel on desktop.
          It sets the hero's height, so the film ends a comfortable distance below the rating. */}
      <div className="relative z-[3] pt-[132px] md:pt-[172px] lg:pt-[460px]" data-id="element-563" data-mp="2p11z5ybwzzf4">
        <div className="mx-auto w-full max-w-[1328px] px-6 md:px-8" data-id="element-564" data-mp="2b93xg8kj95sz">
        <div className="max-w-[760px]" data-id="element-565" data-mp="2fbifwrkai3jq">
          <motion.h1 {...reveal(0.65)} className="font-editorial text-[48px] font-normal leading-[50px] tracking-[-0.015em] md:text-[84px] md:leading-[84px]" data-id="element-566" data-mp="2fa7fn1q1zdxd">
            Anything. Anywhere. Quietly arranged.
          </motion.h1>
          <motion.p {...reveal(0.8)} className="mt-5 max-w-[540px] text-[16px] font-light leading-[25px] text-paper/85 md:text-[18px] md:leading-[28px]" data-id="element-567" data-mp="1sbszqsejukfn">
            Residences, yachts, cars, tables and doors that never open to the public — one call to a personal partner, always under NDA.
          </motion.p>
          <motion.div {...reveal(0.95)} className="mt-8" data-id="element-568" data-mp="3qpw2zu77yeyp">
            <a href="/venturist" className="group relative inline-block pb-1.5 text-[16px] font-medium text-paper md:text-[17px]" data-id="element-569" data-mp="b3vei4p3vi7i">
              {/* Text flips up fully on hover — a copy rises from below */}
              <span className="relative block overflow-hidden" data-id="element-570" data-mp="15z5bifqaxc1">
                <span className="block transition-transform duration-300 ease-silk group-hover:-translate-y-full group-focus-visible:-translate-y-full motion-reduce:transition-none" data-id="element-571" data-mp="17ill91dx8dt">
                  Discover Venturist 360°
                </span>
                <span aria-hidden="true" className="absolute inset-0 block translate-y-full transition-transform duration-300 ease-silk group-hover:translate-y-0 group-focus-visible:translate-y-0 motion-reduce:transition-none" data-id="element-572" data-mp="ytzfs5etn5vc">
                  Discover Venturist 360°
                </span>
              </span>
              {/* Underline: faint track, filled left to right on hover */}
              <span className="absolute inset-x-0 bottom-0 h-px bg-paper/35" aria-hidden="true" data-id="element-573" data-mp="39n28lqbgpukf" />
              <span className="absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-paper transition-transform duration-300 ease-silk group-hover:scale-x-100 group-focus-visible:scale-x-100 motion-reduce:transition-none rtl:origin-right" aria-hidden="true" data-id="element-574" data-mp="320eysudurwqk" />
            </a>
          </motion.div>
        </div>
        </div>
      </div>

      {/* Film control, bottom-right */}
      <div className="absolute inset-x-0 bottom-6 z-[4] md:bottom-8" data-id="element-575" data-mp="2j95lsb4vipu8">
      <div className="mx-auto flex w-full max-w-[1328px] justify-end px-6 md:px-8" data-id="element-576" data-mp="2edyr1stl0vhg">
        <button type="button" onClick={() => setPlaying(p => !p)} aria-label={playing ? t('hero.pause') : t('hero.play')} className="mb-1 p-1 text-paper/70 transition-colors duration-150 hover:text-paper" data-id="element-577" data-mp="2t23c33g3xj3e">
          {playing ? <PauseIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-578" data-mp="1vmfnnkvnbfew" /> : <PlayIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-579" data-mp="1vjthnpoel7ys" />}
        </button>
      </div>
      </div>
    </section>;
}